import React, { useCallback, useContext, useMemo, useState } from 'react';
import CheckBoxIcon from '@mui/icons-material//CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material//CheckBoxOutlineBlank';
import { styled } from '@mui/material';
import { Checkbox, TextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { isNull } from 'lodash';
import { DocumentReferenceObjectCreate } from 'api';
import { FormDialog } from 'components';
import { InformationDashboard409AContext } from 'context/InformationDashboard409AContext';
import { REFERENCES_DATA } from 'pages/Documents/components/EditDocumentDialog/constants';
import { DocumentProgress, EditFileDialogProps, FormValuesInterface, OptionInterface } from './types';

const PREFIX = 'EditFileDialog';

const classes = {
  references: `${PREFIX}-references`,
};

const StyledFormDialog = styled(FormDialog)({
  [`& .${classes.references}`]: {
    marginTop: '2rem',
  },
});

const EditFileDialog: React.FC<EditFileDialogProps> = ({
  open,
  onClose,
  file,
  fileRequests,
  fileRequestMap,
  onSave,
}) => {
  const [fileName, setFileName] = useState(file.file.filename);

  const { featuresObjects } = useContext(InformationDashboard409AContext);

  const originalValues = useMemo(
    () => ({
      references: file.referenced_in?.map(reference => {
        const relatedRef = REFERENCES_DATA.find(refData => refData.name === reference.reference_object_type);
        return {
          objectId: reference.id,
          isDisabled: false,
          reference_object_id: reference.reference_object_id,
          reference_object_type: reference.reference_object_type,
          name: reference.reference_object_type,
          slug: reference.reference_object_type,
          type: relatedRef?.type,
        };
      }) as OptionInterface[],
    }),
    [file]
  );

  const [formValues, setFormValues] = useState<FormValuesInterface>(originalValues);

  const [isSaving, setIsSaving] = useState(false);

  const initiallySelectedFileRequests = useMemo(() => {
    if (file.file.id) {
      return fileRequestMap[file.file.id]?.map(request => request.id) ?? [];
    }
    return [];
  }, [fileRequestMap, file.file.id]);

  const [selectedFileRequestIds, setSelectedFileRequestIds] = useState<number[]>(initiallySelectedFileRequests);

  const handleFileNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFileName(event.target.value);
  };

  const options = useMemo<OptionInterface[]>(
    () =>
      REFERENCES_DATA.map(reference => {
        const referencedFeatureId = featuresObjects ? featuresObjects[reference.slug] : null;
        const isDisabled = isNull(referencedFeatureId);
        return {
          ...reference,
          objectId: referencedFeatureId,
          isDisabled,
        };
      }) as OptionInterface[],
    [featuresObjects]
  );

  const handleReferenceChange = useCallback((__event: React.ChangeEvent<object>, data: OptionInterface[]) => {
    setFormValues(currentFormValues => ({
      ...currentFormValues,
      references: data,
    }));
  }, []);

  const handleFileRequestChange = useCallback((__event: React.ChangeEvent<object>, data: DocumentProgress[]) => {
    setSelectedFileRequestIds(data.map(request => request.id));
  }, []);

  const handleSubmit = useCallback(async () => {
    const deselectedFileRequestIds = initiallySelectedFileRequests.filter(id => !selectedFileRequestIds.includes(id));
    const newlySelectedFileRequestIds = selectedFileRequestIds.filter(
      id => !initiallySelectedFileRequests.includes(id)
    );
    const referencesToAdd
      = formValues.references
        ?.filter(
          reference =>
            !originalValues.references?.length
            || !originalValues.references?.some(ovReference => {
              const referenceId = reference.objectId;
              const ovReferenceId = ovReference.objectId || ovReference.reference_object_id;
              return referenceId === ovReferenceId;
            })
        )
        .map(reference => ({
          reference_object_type: reference.type,
          reference_object_id: reference.objectId,
          document_reference_id: file.document_reference_id,
        })) || ([] as DocumentReferenceObjectCreate[]);

    const referencesToRemove
      = originalValues.references
        ?.filter(
          reference =>
            !formValues.references?.some(nReference => {
              const referenceId = reference.objectId;
              const ovReferenceId = nReference.objectId || nReference.reference_object_id;
              return referenceId === ovReferenceId;
            })
        )
        ?.map(reference => reference.objectId) || [];
    setIsSaving(true);
    await onSave(
      { ...file.file, filename: fileName },
      newlySelectedFileRequestIds,
      deselectedFileRequestIds,
      referencesToAdd as DocumentReferenceObjectCreate[],
      referencesToRemove
    );
    setIsSaving(false);
  }, [initiallySelectedFileRequests, selectedFileRequestIds, originalValues, formValues, onSave, file, fileName]);

  return (
    <StyledFormDialog
      open={open}
      onClose={onClose}
      title="Edit File"
      customButtonLabel="Done"
      onSave={handleSubmit}
      isLoading={isSaving}
      isValid>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <TextField label="File Name" value={fileName} onChange={handleFileNameChange} fullWidth variant="standard" />

        <Autocomplete
          id="references"
          multiple
          className={classes.references}
          options={options}
          getOptionDisabled={option => option.isDisabled}
          disableCloseOnSelect
          getOptionLabel={option => option.name}
          value={formValues.references || []}
          onChange={handleReferenceChange}
          renderOption={(props, option) => {
            const isSelected = (formValues.references || []).some(
              ref => (ref.reference_object_id ?? ref.objectId) === option.objectId
            );
            return (
              <li {...props} key={option.objectId}>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  style={{ marginRight: 8 }}
                  checked={isSelected}
                  disabled={option.isDisabled}
                />
                {option.name}
              </li>
            );
          }}
          style={{ width: '100%' }}
          renderInput={params => <TextField {...params} variant="outlined" label="References" />}
        />

        <Autocomplete
          id="file-requests"
          multiple
          options={fileRequests}
          getOptionLabel={option => option.name}
          value={fileRequests.filter(request => selectedFileRequestIds.includes(request.id))}
          onChange={handleFileRequestChange}
          renderOption={(props, option) => {
            const isSelected = selectedFileRequestIds.includes(option.id);
            return (
              <li {...props} key={option.id}>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  style={{ marginRight: 8 }}
                  checked={isSelected}
                />
                {option.name}
              </li>
            );
          }}
          style={{ width: '100%', marginTop: '2rem' }}
          renderInput={params => <TextField {...params} variant="outlined" label="File Requests" />}
        />
      </div>
    </StyledFormDialog>
  );
};

export default EditFileDialog;
